import { RadioButton, RadioButtonGroup } from "@octopusdeploy/design-system-components";
import type { ActionTemplateParameterResource, ProcessTemplateParameterResource } from "@octopusdeploy/octopus-server-client";
import { ControlType, PackageSelectionMode } from "@octopusdeploy/octopus-server-client";
import { StepTemplatesParameterSelector } from "~/areas/library/components/StepTemplates/StepTemplatesParameterSelector";
import { ProducerTemplatesParameterSelector } from "~/areas/platformHub/processEditor/ProducerTemplatesParameterSelector";
import { useOptionalProducerTemplateProcessContext } from "~/areas/projects/components/Process/ProjectTemplates/useProducerTemplateProcessContext";
import BaseComponent from "~/components/BaseComponent";
import { Note } from "~/components/form";
import type { PackageSelectorProps } from "./PackageSelector";
import PackageSelector from "./PackageSelector";
interface DeferredPackageSelectorProps extends PackageSelectorProps {
    packageSelectionMode: PackageSelectionMode;
    packageSelectionModeError?: string;
    parameters?: ActionTemplateParameterResource[];
    packageParameterName: string;
    packageParameterError?: string;
    onPackageSelectionModeChange(value: PackageSelectionMode): void;
    onPackageParameterChange(value: string): void;
    isAlwaysDeferredPackage?: boolean;
    onParameterAdded?: ((parameters: ProcessTemplateParameterResource[]) => void) | undefined;
}
export default class DeferredPackageSelector extends BaseComponent<DeferredPackageSelectorProps> {
    constructor(props: DeferredPackageSelectorProps) {
        super(props);
    }
    render() {
        return <Selector {...this.props}/>;
    }
    static displayName = "DeferredPackageSelector";
}
function Selector(props: DeferredPackageSelectorProps) {
    const producerTemplateContext = useOptionalProducerTemplateProcessContext();
    if (props.projectId && !producerTemplateContext?.template) {
        return <PackageSelector {...props}/>;
    }
    const packageSelectionMode = props.packageSelectionMode || PackageSelectionMode.Immediate;
    const onChange = (value: PackageSelectionMode) => props.onPackageSelectionModeChange(value);
    const templateParameters = producerTemplateContext?.parameters || [];
    if (props.isAlwaysDeferredPackage && packageSelectionMode === PackageSelectionMode.Deferred) {
        return <PackageParameterSelector {...{ ...props, templateParameters }}/>;
    }
    return (<RadioButtonGroup value={packageSelectionMode} error={props.packageSelectionModeError} onChange={onChange}>
            {<RadioButton value={PackageSelectionMode.Immediate} label="Select specific package"/>}
            {packageSelectionMode === PackageSelectionMode.Immediate && <PackageSelector {...props}/>}
            {<RadioButton value={PackageSelectionMode.Deferred} label="Let the project select the package"/>}
            {packageSelectionMode === PackageSelectionMode.Deferred && <PackageParameterSelector {...{ ...props, templateParameters }}/>}
        </RadioButtonGroup>);
}
function PackageParameterSelector(props: DeferredPackageSelectorProps) {
    const noteText = props.isAlwaysDeferredPackage ? "When using this process template in a project, the user will be prompted to select a package." : "When using this step template in a process, the user will be prompted to select a package.";
    return (<div>
            {props.isAlwaysDeferredPackage ? (<ProducerTemplatesParameterSelector selectedParameter={props.packageParameterName || null} onParameterChange={(value) => props.onPackageParameterChange(value || "")} controlType={ControlType.Package} allowClear={true} error={props.packageParameterError} bindValue={false} onParameterAdded={props.onParameterAdded}/>) : (<StepTemplatesParameterSelector selectedParameter={props.packageParameterName || null} onParameterChange={(value) => props.onPackageParameterChange(value || "")} controlType={ControlType.Package} allowClear={true} error={props.packageParameterError} parameters={props.parameters || []}/>)}
            <Note>{noteText}</Note>
        </div>);
}
